import type { HazWasteCodeBasis, HazWasteGeneratorStatus } from '../../tri-shared/types/db.ts';
import { EPA_WASTE_CODES } from '../waste-codes.ts';

const ACUTE_F_LISTED_CODES = new Set(['F020', 'F021', 'F022', 'F023', 'F026', 'F027']);
const EPA_WASTE_CODE_SET = new Set(EPA_WASTE_CODES.map((wasteCode) => wasteCode.code));

export function normalizeEpaWasteCodes(codes: readonly string[]): string[] {
  return Array.from(new Set(codes.map((code) => code.trim().toUpperCase()).filter(Boolean))).sort();
}

export function invalidEpaWasteCodes(codes: readonly string[]): string[] {
  return normalizeEpaWasteCodes(codes).filter((code) => !EPA_WASTE_CODE_SET.has(code));
}

export function assertValidEpaWasteCodes(codes: readonly string[]): void {
  const invalidCodes = invalidEpaWasteCodes(codes);
  if (invalidCodes.length > 0) throw new Error(`Invalid EPA waste code(s): ${invalidCodes.join(', ')}.`);
}

export function deriveWasteCodeBasis(codes: readonly string[]): HazWasteCodeBasis {
  const normalized = normalizeEpaWasteCodes(codes);
  assertValidEpaWasteCodes(normalized);
  const hasCharacteristic = normalized.some((code) => /^D\d{3}$/.test(code));
  const hasListed = normalized.some((code) => /^[FKPU]\d{3}$/.test(code));
  if (hasCharacteristic && hasListed) return 'both';
  if (hasCharacteristic) return 'characteristic';
  return 'listed';
}

export function isAcuteHazardousWasteCode(code: string): boolean {
  const normalized = code.trim().toUpperCase();
  return /^P\d{3}$/.test(normalized) || ACUTE_F_LISTED_CODES.has(normalized);
}

export function isAcuteHazardousFromCodes(codes: readonly string[]): boolean {
  return codes.some(isAcuteHazardousWasteCode);
}

/** Federal monthly generator category thresholds from 40 CFR 262.13 Table 1. */
export function computeHazWasteGeneratorStatus(
  totalNonAcuteKg: number,
  totalAcuteKg: number,
  totalAcuteSpillCleanupResidueKg = 0,
): HazWasteGeneratorStatus {
  if (totalAcuteKg > 1 || totalNonAcuteKg >= 1000 || totalAcuteSpillCleanupResidueKg > 100) return 'lqg';
  if (totalNonAcuteKg > 100) return 'sqg';
  if (totalNonAcuteKg > 0 || totalAcuteKg > 0 || totalAcuteSpillCleanupResidueKg > 0) return 'vsqg';
  return 'not-generator';
}
